import { useState } from "react";
import { useScrollReveal, getRevealClasses } from "@/hooks/useScrollReveal";
import { faqData } from "@/mocks/homepageData";

function FaqItem({
  question,
  answer,
  isOpen,
  onToggle,
}: {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return (
    <div
      itemScope
      itemProp="mainEntity"
      itemType="https://schema.org/Question"
      className={`rounded-xl border transition-all duration-300 ${
        isOpen
          ? "border-[#7C3AED]/40 bg-[#1F2937]"
          : "border-[#374151] bg-[#1F2937]/60 hover:border-[#475569]"
      }`}
    >
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 md:px-6 md:py-5 text-left"
      >
        <h3
          itemProp="name"
          className="text-[#F8FAFC] font-semibold text-sm md:text-base leading-snug"
        >
          {question}
        </h3>
        <div
          className={`w-8 h-8 md:w-9 md:h-9 rounded-lg flex items-center justify-center flex-shrink-0 transition-all duration-300 ${
            isOpen
              ? "bg-[#7C3AED] text-white rotate-180"
              : "bg-[#374151] text-[#94A3B8]"
          }`}
        >
          <i className="ri-arrow-down-s-line text-lg" />
        </div>
      </button>

      <div
        className={`overflow-hidden transition-all duration-300 ${
          isOpen ? "max-h-[600px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div
          itemScope
          itemProp="acceptedAnswer"
          itemType="https://schema.org/Answer"
          className="px-5 pb-5 md:px-6 md:pb-6 pt-0"
        >
          <div className="h-px bg-[#374151] mb-4" />
          <p
            itemProp="text"
            className="text-[#94A3B8] text-sm md:text-base leading-relaxed"
          >
            {answer}
          </p>
        </div>
      </div>
    </div>
  );
}

export default function FaqSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const { ref: headerRef, isVisible: headerVisible } = useScrollReveal(0.1);
  const { ref: listRef, isVisible: listVisible } = useScrollReveal(0.1);

  return (
    <section className="py-20 md:py-28 bg-[#0B0F19]">
      <div className="max-w-4xl mx-auto px-4 md:px-6">
        {/* Section Header */}
        <div
          ref={headerRef}
          className={`text-center mb-12 md:mb-16 transition-all duration-700 ${getRevealClasses(
            headerVisible,
            "up"
          )}`}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#7C3AED]/40 mb-6">
            <i className="ri-questionnaire-line text-[#7C3AED]" />
            <span className="text-sm text-[#F8FAFC]">Common Questions</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#F8FAFC] mb-4">
            Frequently Asked{" "}
            <span className="text-gradient">Questions</span>
          </h2>
          <p className="text-[#94A3B8] text-lg max-w-2xl mx-auto">
            Everything you need to know about XM Trading before opening an
            account.
          </p>
        </div>

        {/* FAQ Accordion with Schema.org FAQPage */}
        <div
          ref={listRef}
          itemScope
          itemType="https://schema.org/FAQPage"
          className={`space-y-3 md:space-y-4 transition-all duration-700 ${getRevealClasses(
            listVisible,
            "up"
          )}`}
        >
          {faqData.map((item, index) => (
            <FaqItem
              key={index}
              question={item.question}
              answer={item.answer}
              isOpen={openIndex === index}
              onToggle={() =>
                setOpenIndex(openIndex === index ? null : index)
              }
            />
          ))}
        </div>
      </div>
    </section>
  );
}